import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex items-center space-x-4 p-4" }
const _hoisted_2 = { class: "flex items-center justify-center h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-medium text-gray-900" }, "Select Merge Field", -1)
const _hoisted_4 = { class: "flex flex-col space-y-4 px-4 py-2" }
const _hoisted_5 = { class: "max-h-72 overflow-y-auto border border-primary-color-300 rounded-lg" }
const _hoisted_6 = { class: "h-64" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex justify-between space-x-4 p-4" }
const _hoisted_9 = { class: "flex justify-start" }
const _hoisted_10 = { class: "flex justify-end flex-grow" }

import { computed, onMounted, ref } from "vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import { capitalize, getIconByType, toPascalCase } from "@/shared/utils/helpers"
import { useConfigStore } from "@/stores/config"
import { useFormStore } from "@/stores/form"
import { Prototype } from "@/shared/models/config"
import { Attribute, AttributeType } from "@/shared/models/attributes"
import { CascadeChildOption } from "@/shared/models/cascade-option"
import {
    CogIcon,
    DocumentTextIcon,
    LinkIcon,
    OfficeBuildingIcon,
    UserCircleIcon,
    UserIcon,
} from "@heroicons/vue/outline"
import router from "@/router"

// Props
interface Props {
    open: boolean
    isForRichEditor?: boolean
    specialVariable?: string
    isTemplateTypeEmail?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsMergeFields',
  props: {
    open: { type: Boolean },
    isForRichEditor: { type: Boolean },
    specialVariable: {},
    isTemplateTypeEmail: { type: Boolean }
  },
  emits: ["closeModal", "addMergeField"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

// Stores
const configStore = useConfigStore()
const formStore = useFormStore()

// States
const searchQuery = ref("")
const navigationPath = ref<{ name: string; options: CascadeChildOption[] }[]>([])
const currentOptions = ref<CascadeChildOption[]>([])

// Filtered options based on search query
const filteredOptions = computed(() => {
    return currentOptions.value
        .filter((option) => option.name.toLowerCase().includes(searchQuery.value.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name))
})

// Generate options hierarchy
function generateOptions() {
    const mergeFields = categories.value
        .map((category) => ({
            name: capitalize(category.typeName),
            value: category.typeName,
            children: setChildElements(category),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

    if (props.isTemplateTypeEmail) {
        mergeFields.push({
            name: "System",
            value: "system",
            children: Object.values(systemVariables),
        })
    }

    if (props.specialVariable) {
        mergeFields.push({
            name: "User",
            value: "user",
            children: setSpecialVariables(),
        })
    }

    return mergeFields
}

const isWorkflowsRoute = computed(() => router.currentRoute.value.path.includes("workflows"))

// Categories for options
const categories = computed(() => {
    let prototypeNames = ["contact", "agent", "agency"]
    if (isWorkflowsRoute.value) {
        prototypeNames = ["contact", "agent", "agency", "form", "call"]
    }
    return configStore.prototypes.filter((prototype) => prototypeNames.includes(prototype.typeName))
})

// Define system variables and special variables
const systemVariables = {
    Unsubscribed: { name: "Unsubscribe Link", value: "unsubscribeLink", hasFallback: false },
}

const specialVariableMap = {
    "Welcome user": { name: "Verify Email URL", value: "user.verifyEmailUrl", hasFallback: false },
    "Forgotten password": { name: "Reset Password URL", value: "user.resetPasswordUrl", hasFallback: false },
    "SMS daily quota reached": {
        name: "Setup Business Profile URL",
        value: "user.setupBusinessProfileURL",
        hasFallback: false,
    },
    "Contact import finished": [
        { name: "Contact Import Status", value: "user.status", hasFallback: false },
        { name: "Number of inserted contacts", value: "user.inserted", hasFallback: false },
        { name: "Errors", value: "user.errors", hasFallback: false },
    ],
    "Invite agent": { name: "Invite Link", value: "user.inviteLink", hasFallback: false },
}

// Fetch forms from the store
const forms = computed(() => formStore.tableState.data)

// Function to set child elements based on category
function setChildElements(category: Prototype) {
    const children =
        category.attributes
            ?.map((attribute: Attribute) => ({
                name: attribute.displayName,
                value: `${category.typeName}.${attribute.name}`,
                type: attribute.type,
                hasFallback: true,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) ?? []

    if (category.typeName === "agent") {
        children.unshift({
            name: "Signature",
            value: "{templateRenderedSignature}",
            type: AttributeType.String,
            hasFallback: false,
        })
    }

    if (category.typeName === "form") {
        children.splice(0, children.length)

        forms.value.forEach((form) => {
            children.push({
                name: form.title,
                value: `form.${form.title}`,
                hasFallback: false,
                type: AttributeType.Object,
                children: form.sections.flatMap((section) =>
                    section.elements.flatMap((element) =>
                        element.labels.map((label) => ({
                            name: label,
                            value: `form.${toPascalCase(form.title)}.${element.name}`,
                            type: element.__t,
                            hasFallback: false,
                        }))
                    )
                ),
            })
        })
    }

    return children
}

// Function to generate special variables
function setSpecialVariables() {
    const specialVariable = specialVariableMap[props.specialVariable]
    return Array.isArray(specialVariable) ? specialVariable : [specialVariable]
}

// Handle option click
function handleOptionClick(option: CascadeChildOption) {
    if (option.children) {
        navigationPath.value.push({ name: option.name, options: currentOptions.value })
        currentOptions.value = option.children
        searchQuery.value = ""
    } else {
        // Add placeholder fallback value
        const fallbackPlaceholder = "<fallback>"
        emit("addMergeField", `{{${option.value}:${fallbackPlaceholder}}}`)
        closeModal()
        searchQuery.value = ""
    }
}

// Handle back navigation
function goBack() {
    const lastPath = navigationPath.value.pop()
    if (lastPath) currentOptions.value = lastPath.options
    searchQuery.value = ""
}

// Close modal
function closeModal() {
    emit("closeModal")
    setTimeout(() => {
        navigationPath.value = []
        currentOptions.value = generateOptions()
    }, 300)
    searchQuery.value = ""
}

function getIcon(option: CascadeChildOption) {
    if (option.type) {
        return getIconByType(option.type, option.value.includes(".") ? option.value.split(".")[1] : option.value)
    }
    switch (option.value) {
        case "contact":
            return UserIcon
        case "agent":
            return UserCircleIcon
        case "agency":
            return OfficeBuildingIcon
        case "system":
            return CogIcon
        case "unsubscribeLink":
            return LinkIcon
    }
}

// On mount, generate options
onMounted(async () => {
    if (formStore.tableState.data.length === 0) {
        await formStore.getForms()
    }
    currentOptions.value = generateOptions()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GgmsModal, {
    open: props.open,
    onCloseModal: closeModal,
    style: { 'z-index': 99999 }
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(DocumentTextIcon), { class: "h-6 w-6 text-primary-color-600" })
        ]),
        _hoisted_3
      ])
    ]),
    under: _withCtx(() => [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          (navigationPath.value.length)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 0,
                styleType: "secondary",
                onClick: goBack
              }, {
                default: _withCtx(() => [
                  _createTextVNode("← Back")
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(GgmsButton, {
            styleType: "secondary",
            onClick: closeModal
          }, {
            default: _withCtx(() => [
              _createTextVNode("Cancel")
            ]),
            _: 1
          })
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
          type: "text",
          placeholder: "Search values",
          class: "p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-primary-color-300"
        }, null, 512), [
          [_vModelText, searchQuery.value]
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("ul", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option) => {
              return (_openBlock(), _createElementBlock("li", {
                key: option.value,
                class: "py-3 px-4"
              }, [
                _createElementVNode("button", {
                  class: "flex gap-x-2 text-left items-center w-full text-gray-900 font-medium bg-transparent hover:bg-primary-color-100 rounded p-1 focus:outline-none",
                  onClick: ($event: any) => (handleOptionClick(option))
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(getIcon(option)), { class: "w-5 h-5 text-gray-400 stroke-gray-400" })),
                  _createTextVNode(" " + _toDisplayString(option.name), 1)
                ], 8, _hoisted_7)
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})